import axios from 'axios';
import { localDateToUtcRange, getCurrentLocalDate } from './utils/timezone';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const errorData: AuthErrorResponse = error.response.data;
      
      // Handle specific authentication error codes
      if (errorData?.code) {
        switch (errorData.code) {
          case 'SESSION_EXPIRED':
            console.log('Session expired, redirecting to login');
            handleAuthFailure('Your session has expired. Please log in again.');
            break;
          case 'AUTH_REQUIRED':
          case 'AUTH_INVALID':
            console.log('Authentication failed, redirecting to login');
            handleAuthFailure('Please log in to continue.');
            break;
          default:
            console.log('Unknown auth error, redirecting to login');
            handleAuthFailure('Authentication required. Please log in.');
        }
      } else {
        // Fallback for generic 401 responses
        handleAuthFailure('Your session has expired. Please log in again.');
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to handle authentication failures
function handleAuthFailure(message: string) {
  // Clear stored token
  localStorage.removeItem('authToken');
  
  // You could also clear other user-related data here
  // localStorage.removeItem('userEmail');
  
  // Show notification if you have a notification system
  // You might want to replace this with your actual notification system
  console.warn(message);
  
  // Redirect to login page
  // Add a timeout to allow any pending operations to complete
  setTimeout(() => {
    const currentPath = window.location.pathname;
    // Avoid infinite redirects if already on login page
    if (currentPath !== '/login' && currentPath !== '/') {
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}&message=${encodeURIComponent(message)}`;
    } else {
      window.location.href = '/login';
    }
  }, 100);
}

// Helper function to handle auth errors in fetch-based requests
async function handleFetchAuthError(response: Response): Promise<void> {
  if (response.status === 401) {
    try {
      const errorData: AuthErrorResponse = await response.json();
      
      if (errorData?.code) {
        switch (errorData.code) {
          case 'SESSION_EXPIRED':
            handleAuthFailure('Your session has expired. Please log in again.');
            break;
          case 'AUTH_REQUIRED':
          case 'AUTH_INVALID':
            handleAuthFailure('Please log in to continue.');
            break;
          default:
            handleAuthFailure('Authentication required. Please log in.');
        }
      } else {
        handleAuthFailure('Your session has expired. Please log in again.');
      }
    } catch {
      // If we can't parse the error response, still handle auth failure
      handleAuthFailure('Your session has expired. Please log in again.');
    }
  }
}

export interface User {
  id: number;
  email: string;
}

export interface MealItem {
  id?: number;
  user_id?: number;
  food_name: string;
  qty?: number;
  unit?: string;
  notes?: string;
  macros: {
    carbs: number;
    protein: number;
    fat: number;
    calories: number;
  };
  meal_time: string;
  dish_session_id?: string; // Groups related items together (e.g., breakfast sandwich ingredients)
  dish_template_id?: number; // Links to dish template if created from template
  dish_name?: string; // AI-generated or template dish name
  conversation_id?: string; // Links to chat conversation that created this item
  is_variation?: boolean; // True if created as variation of template
  created_at?: string;
  updated_at?: string;
}

// Keep old Meal interface for backward compatibility during transition
export interface Meal {
  id?: number;
  user_id?: number;
  description: string;
  carbs: number;
  protein: number;
  fat: number;
  calories: number;
  meal_time: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  message?: string;
  error?: string;
}

// Authentication error response interface
export interface AuthErrorResponse {
  error: string;
  code: 'SESSION_EXPIRED' | 'AUTH_REQUIRED' | 'AUTH_INVALID' | 'ADMIN_REQUIRED';
  message: string;
}

export interface MealResponse {
  success: boolean;
  meal?: Meal;
  meals?: Meal[];
  error?: string;
}

export interface MealItemResponse {
  success: boolean;
  items?: MealItem[];
  error?: string;
}

export const authAPI = {
  async sendCode(email: string): Promise<AuthResponse> {
    const response = await api.post('/auth/code', { email });
    return response.data;
  },

  async verifyCode(email: string, code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/verify', { email, code });
    return response.data;
  },
};

export const mealAPI = {
  async createMeal(meal: Omit<Meal, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<MealResponse> {
    const response = await api.post('/meals', meal);
    return response.data;
  },

  async updateMeal(mealId: number, updates: Partial<Meal>): Promise<MealResponse> {
    const response = await api.put(`/meals/${mealId}`, updates);
    return response.data;
  },

  async deleteMeal(mealId: number): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await api.delete(`/meals/${mealId}`);
    return response.data;
  },

  async getMeals(date?: string): Promise<MealResponse> {
    let params = {};
    if (date) {
      // Convert local date to UTC range for timezone-aware filtering
      const { startUtc, endUtc } = localDateToUtcRange(date);
      params = { startUtc, endUtc };
    }
    const response = await api.get('/meals', { params });
    return response.data;
  },

  async getMealItems(date?: string): Promise<MealItemResponse> {
    let params = {};
    if (date) {
      // Convert local date to UTC range for timezone-aware filtering
      const { startUtc, endUtc } = localDateToUtcRange(date);
      params = { startUtc, endUtc };
    }
    const response = await api.get('/meals', { params });
    return response.data;
  },

  async updateMealItem(itemId: number, updates: { food_name?: string; qty?: number; unit?: string; notes?: string; carbs?: number; protein?: number; fat?: number; calories?: number }): Promise<{ success: boolean; item?: MealItem; error?: string }> {
    const response = await api.put(`/meals/${itemId}`, updates);
    return response.data;
  },

  async deleteMealItem(itemId: number): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await api.delete(`/meals/${itemId}`);
    return response.data;
  },

  async updateDishTemplate(templateId: number, updates: { template_name?: string }): Promise<{ success: boolean; error?: string }> {
    const response = await api.put(`/dish-templates/${templateId}`, updates);
    return response.data;
  },

  async deleteDishTemplate(templateId: number): Promise<{ success: boolean; error?: string }> {
    const response = await api.delete(`/dish-templates/${templateId}`);
    return response.data;
  },
};

export const chatAPI = {
  async streamMessage(message: string, conversationId?: string, selectedDate?: string, image?: string, messageHistory?: Array<{role: string, content: string}>): Promise<ReadableStream<Uint8Array>> {
    // Convert selectedDate to UTC range for proper timezone handling
    let dateRange: { startUtc: string; endUtc: string } | undefined;
    let selectedDateTime: string | undefined;
    if (selectedDate) {
      dateRange = localDateToUtcRange(selectedDate);

      // Calculate selected date with current local time for meal_time
      const now = new Date();
      const [year, month, day] = selectedDate.split('-').map(Number);
      const selectedDateWithCurrentTime = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
      selectedDateTime = selectedDateWithCurrentTime.toISOString();
    }

    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ message, conversationId, selectedDate, dateRange, selectedDateTime, image, messageHistory }),
    });

    // Handle authentication errors
    if (response.status === 401) {
      await handleFetchAuthError(response);
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    return response.body;
  },

  async streamAdvisorMessage(message: string, conversationId?: string, image?: string): Promise<ReadableStream<Uint8Array>> {
    const response = await fetch(`${API_BASE_URL}/advisor/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ message, conversationId, image }),
    });

    // Handle authentication errors
    if (response.status === 401) {
      await handleFetchAuthError(response);
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    return response.body;
  },

  /**
   * Upload a user-selected image to the backend which stores it in Cloudflare R2 and
   * returns a publicly-accessible URL. The backend expects multipart/form-data with
   * a single field named "file".
   */
  async uploadImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: formData,
    });

    // Handle auth errors for fetch requests
    if (response.status === 401) {
      await handleFetchAuthError(response);
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      throw new Error(`Image upload failed: ${response.status}`);
    }

    const data = await response.json() as { success: boolean; url: string };
    if (!data.success || !data.url) {
      throw new Error('Image upload failed');
    }

    return data.url;
  },
};

export interface AdminUser {
  id: number;
  email: string;
  created_at: string;
  last_login: string;
}

export interface DailySummary {
  date: string;
  total_carbs: number;
  total_protein: number;
  total_fat: number;
  total_calories: number;
  item_count: number;
}

export interface AdminUsersResponse {
  success: boolean;
  users: AdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: string;
}

export interface AdminUserDetailsResponse {
  success: boolean;
  user: AdminUser;
  dailySummaries: DailySummary[];
  error?: string;
}

export interface AdminUserDayDetailsResponse {
  success: boolean;
  items: MealItem[];
  date: string;
  error?: string;
}

export interface UserTargets {
  carbs?: number;
  protein?: number;
  fat?: number;
}

export interface UserTargetsResponse {
  success: boolean;
  targets: UserTargets;
  error?: string;
}

export interface AdvisorAnalysis {
  today: {
    carbs: number;
    protein: number;
    fat: number;
    calories: number;
  };
  weeklyAverage: {
    carbs: number;
    protein: number;
    fat: number;
    calories: number;
  };
  targets: UserTargets;
}

export interface AdvisorAnalysisResponse {
  success: boolean;
  analysis: AdvisorAnalysis;
  error?: string;
}

export const userAPI = {
  async getTargets(): Promise<UserTargetsResponse> {
    const response = await api.get('/user/targets');
    return response.data;
  },

  async updateTargets(targets: UserTargets): Promise<UserTargetsResponse> {
    const response = await api.put('/user/targets', targets);
    return response.data;
  },
};

export const advisorAPI = {
  async getAnalysis(): Promise<AdvisorAnalysisResponse> {
    // Send current local date for timezone-aware analysis
    const today = getCurrentLocalDate();
    const { startUtc, endUtc } = localDateToUtcRange(today);
    const response = await api.get('/advisor/analysis', { 
      params: { startUtc, endUtc, localDate: today }
    });
    return response.data;
  },
};

export const adminAPI = {
  async getUsers(page: number = 1, limit: number = 100): Promise<AdminUsersResponse> {
    const response = await api.get(`/admin/users?page=${page}&limit=${limit}`);
    return response.data;
  },

  async getUserDetails(userId: number): Promise<AdminUserDetailsResponse> {
    // Get user's timezone for proper date grouping
    const timezoneOffset = new Date().getTimezoneOffset();
    const offsetString = timezoneOffset > 0 ? `-${Math.floor(timezoneOffset/60).toString().padStart(2, '0')}:${(timezoneOffset%60).toString().padStart(2, '0')}` : `+${Math.floor(Math.abs(timezoneOffset)/60).toString().padStart(2, '0')}:${(Math.abs(timezoneOffset)%60).toString().padStart(2, '0')}`;
    
    const response = await api.get(`/admin/users/${userId}?timezone=${encodeURIComponent(offsetString)}`);
    return response.data;
  },

  async getUserDayDetails(userId: number, date: string): Promise<AdminUserDayDetailsResponse> {
    const response = await api.get(`/admin/users/${userId}/days/${date}`);
    return response.data;
  },
};

export default api;